// React
import React, { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import classnames from 'classnames';
import { useHistory, useParams } from 'react-router-dom';
import {
  Col,
  Row,
  Nav,
  Card,
  Form,
  NavItem,
  NavLink,
  TabPane,
  CardFooter,
  CardHeader,
  TabContent,
} from 'reactstrap';

// Hooks
import { useValidationOfProposals } from '../../hooks/useValidationOfProposals';

// Yup validation
import * as Yup from 'yup';
import { useFormik } from 'formik';

// Containers Injections
import {
  PageWrapper,
  notifyError,
  ModalPendency,
  notifySuccess,
  LoadingScreen,
  convertEventSelectToTarget,
} from '../../containers/componentsInjection';
import { ValidationOfProposalsInjetion } from '../../containers/validationOfProposalsInjection';

// Tab's views
import { ClientDataTab } from './tabs/clientDataTab';
import { ProposalDataTab } from './tabs/proposalDataTab';

// Utils
import { APIClient } from '../../../../helpers/api_helper';
import { filterDependents } from '../../utils/validation/filter-dependents-empty-values';

export const ValidationOfProposalsForm = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [dependents, setDependents] = useState([]);
  const [showEmployment, setShowEmployment] = useState(false);
  const [showOrganization, setShowOrganization] = useState(false);
  const [preProposalByHash, setPreProposalByHash] = useState(null);
  const [isValidDependentCpf, setIsValidDependentCpf] = useState([]);
  const [isOpenModalPendency, setIsOpenModalPendency] = useState(false);
  const [isAnyDependentFieldRequired, setIsAnyDependentFieldRequired] =
    useState(
      Array(dependents.length).fill({
        cpf: false,
        name: false,
        birth: false,
        kinship: false,
      })
    );

  const validateBoolean = (value) => value === 't';

  const isSaveButtonDisabled =
    isAnyDependentFieldRequired.some((obj) =>
      Object.values(obj).some((value) => value === true)
    ) || isValidDependentCpf.some((value) => value === false);

  const { id } = useParams();
  const api = new APIClient();
  const history = useHistory();

  const {
    // getPreProposalByHash,
    // preProposalByHash,
    pendingPreProposal,
    // approvePreProposal,
    updatePreProposal,
  } = ValidationOfProposalsInjetion();

  const {
    // Methods
    requestGetPlans,
    requestGetOffices,
    requestGetKinship,
    requestGetDepartments,
    requestGetOrganizations,
    requestGetCategoryPlans,
    requestGetPaymentConditions,
    requestGetMaxDependentInPlans,
    // Data
    plans,
    offices,
    kinships,
    maxDependents,
    organizations,
    categoryPlans,
    situationOptions,
    departmentOptions,
    paymentFrequencyOptions,
    paymentConditionsOptions,
  } = useValidationOfProposals();

  function tabChange(tab) {
    if (activeTab !== tab) setActiveTab(tab);
  }

  function toggleModalPendency() {
    setIsOpenModalPendency(!isOpenModalPendency);
  }

  function getSituationOption(situationId) {
    return situationOptions.find((option) => option.value === situationId);
  }

  const getPreProposalByHash = async (hash) => {
    setLoading(true);

    try {
      const response = await api.get(`/pre-proposal/${hash}`);

      if (response) {
        setPreProposalByHash(response);
        setShowOrganization(validateBoolean(response?.show_organization));
        setShowEmployment(validateBoolean(response?.show_employment_details));
      }
    } catch (error) {
      console.error('Erro ao buscar o pré-proposta:', error);
    }

    setLoading(false);
  };

  const approvePreProposal = (hash) => {
    setLoading(true);

    api
      .create(`/pre-proposal/approve/${hash}`)
      .then((response) => {
        notifySuccess('Pré-proposta aprovada com sucesso!');
        history.push(`/propostas/view/${response.proposalHashId}`);
      })
      .catch((error) => {
        notifyError('Erro ao aprovar pré-proposta');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      // Client Tab
      name: preProposalByHash ? preProposalByHash?.customer_name : '',
      cpf_client: preProposalByHash ? preProposalByHash?.cpf : '',
      rg: preProposalByHash ? preProposalByHash?.rg : '',
      organ: preProposalByHash ? preProposalByHash?.rg_issuer : '',
      motherName: preProposalByHash ? preProposalByHash.mother_name : '',
      birth: preProposalByHash ? preProposalByHash?.birthdate : '',
      gender: preProposalByHash?.gender === 'M' ? 'M' : 'F',
      stateCivilInput: preProposalByHash
        ? {
            label: preProposalByHash?.marital_status_name,
            value: preProposalByHash?.marital_status_id,
          }
        : null,
      email: preProposalByHash ? preProposalByHash?.email : '',
      phone: preProposalByHash ? preProposalByHash?.phones?.phone1 : '',
      tel: preProposalByHash ? preProposalByHash?.phones?.phone2 : '',
      zipcode: preProposalByHash ? preProposalByHash?.zipcode : '',
      street: preProposalByHash ? preProposalByHash?.address1 : '',
      number: preProposalByHash ? preProposalByHash?.address_number : '',
      neighborhood: preProposalByHash ? preProposalByHash?.district : '',
      address2: preProposalByHash ? preProposalByHash?.address2 : '',
      cityInput: preProposalByHash
        ? {
            label: preProposalByHash?.city_name,
            value: preProposalByHash?.city_id,
          }
        : null,
      complement: preProposalByHash ? preProposalByHash?.address2 : '',

      // Proposal Tab
      categoryPlan: preProposalByHash
        ? {
            label: preProposalByHash?.category,
            value: preProposalByHash?.category_id,
          }
        : null,
      plan: preProposalByHash
        ? {
            label: preProposalByHash?.plan,
            value: preProposalByHash?.plan_id,
          }
        : null,
      saleAt: preProposalByHash ? preProposalByHash?.sale_at : '',
      effectiveDate: preProposalByHash?.effective_date || '',
      validUntil: preProposalByHash ? preProposalByHash?.valid_until : '',
      organization: preProposalByHash
        ? {
            label: preProposalByHash?.organization_name,
            value: preProposalByHash?.organization_id,
          }
        : null,
      department: preProposalByHash
        ? {
            label: preProposalByHash?.unit_name,
            value: preProposalByHash?.unit_id,
          }
        : null,
      secretary: preProposalByHash ? preProposalByHash?.department : '',
      situation: preProposalByHash
        ? getSituationOption(preProposalByHash?.cadastral_status)
        : null,
      validContractWork: preProposalByHash
        ? preProposalByHash?.work_contract_expiration
        : '',
      registration: preProposalByHash ? preProposalByHash?.registration : '',
      obs: preProposalByHash ? preProposalByHash?.obs : '',
      funeralAssistance:
        preProposalByHash?.funeral_assistance === 't' ? true : false,
      offices: preProposalByHash
        ? {
            label: preProposalByHash?.office_name,
            value: preProposalByHash?.office_id,
          }
        : null,
      paymentFrequency: preProposalByHash
        ? {
            label:
              preProposalByHash?.periodicity_payment === 'M'
                ? 'Mensal'
                : 'Anual',
            value: preProposalByHash?.periodicity_payment,
          }
        : null,
      paymentConditions: preProposalByHash
        ? {
            label: preProposalByHash?.payment_condition_name,
            value: preProposalByHash?.payment_condition_id,
          }
        : null,
      price: preProposalByHash ? preProposalByHash?.price : '',
      customerPhoto: preProposalByHash ? preProposalByHash?.photo : '',
      docPhoto: preProposalByHash ? preProposalByHash?.photo_doc : '',
      signature: preProposalByHash ? preProposalByHash?.signature : '',
    },
    validationSchema: Yup.object({
      // Client Tab
      name: Yup.string().required('Nome é obrigatório'),
      cpf_client: Yup.string().cpfType('Digite um CPF válido').nullable(),
      rg: Yup.string().nullable(),
      organ: Yup.string().nullable(),
      motherName: Yup.string().nullable(),
      birth: Yup.date().required('Nascimento é obrigatório'),
      gender: Yup.string().required().nullable(),
      stateCivilInput: Yup.object()
        .nullable()
        .required('Estado Civil é obrigatório'),
      email: Yup.string().required('Email é obrigatório'),
      phone: Yup.string().required('Celular é obrigatório'),
      tel: Yup.string().nullable(),
      zipcode: Yup.string().required('CEP é obrigatório'),
      street: Yup.string().required('Logradouro é obrigatório'),
      number: Yup.string().required('Número é obrigatório'),
      neighborhood: Yup.string().required('Bairro é obrigatório'),
      address2: Yup.string().nullable(),
      cityInput: Yup.object().nullable().required('Cidade é obrigatório'),
      complement: Yup.string().nullable(),

      // Proposal Tab
      categoryPlan: Yup.object()
        .shape({
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
        .nullable()
        .required('Selecione uma categoria'),
      plan: Yup.object()
        .shape({
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
        .nullable()
        .required('Selecione um plano'),
      saleAt: Yup.date().required(),
      effectiveDate: Yup.date().required(),
      validUntil: Yup.date().required(),
      organization: Yup.object().nullable(),
      department: Yup.object().nullable(),
      secretary: Yup.string().nullable(),
      situation: Yup.object().nullable(),
      validContractWork: Yup.date().nullable(),
      registration: showEmployment
        ? Yup.string().required('Matrícula é obrigatório')
        : Yup.string().nullable(),
      obs: Yup.string().nullable(),
      funeralAssistance: Yup.boolean().nullable(),
      offices: Yup.object().required(),
      paymentFrequency: Yup.object()
        .shape({
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
        .nullable()
        .required('Selecione uma periodicidade'),
      paymentConditions: Yup.object()
        .shape({
          label: Yup.string().required(),
          value: Yup.string().required(),
        })
        .nullable()
        .required('Selecione uma condição'),
      price: Yup.string().required('Preço é obrigatório'),
      customerPhoto: Yup.string().nullable(),
      docPhoto: Yup.string().nullable(),
      signature: Yup.string().nullable(),
    }),

    onSubmit: (values) => {
      const filteredDependentes = filterDependents(dependents);

      const valuesToSubmit = {
        customer_name: values.name,
        rg: values.rg,
        rg_issuer: values.organ,
        marital_status_id: values.stateCivilInput?.value,
        mother_name: values.motherName || null,
        birthdate: values.birth,
        gender: values.gender,
        phones: {
          phone1: values.phone,
          phone2: values.tel,
        },
        email: values.email,
        address1: values.street,
        address_number: values.number,
        address2: values.complement,
        district: values.neighborhood,
        city_id: values.cityInput?.value,
        zipcode: values.zipcode,
        sale_at: values.saleAt,
        plan_id: values.plan?.value,
        valid_until: values.validUntil,
        effective_date: values.effectiveDate,
        organization_id: values.organization?.value || null,
        department: values.secretary || null,
        cadastral_status: values.situation?.value || null,
        work_contract_expiration: values.validContractWork || null,
        registration: values.registration || null,
        unit_id: values.department?.value || null,
        price: values.price,
        payment_condition_id: values.paymentConditions?.value,
        periodicity_payment: values.paymentFrequency?.value,
        funeral_assistance: values.funeralAssistance === true ? 't' : 'f',
        obs: values.obs,
        dependents: filteredDependentes.length > 0 ? filteredDependentes : [],
      };

      updatePreProposal(id, valuesToSubmit);
    },
  });

  const handleApprovePreProposal = async () => {
    const errors = await validation.validateForm();

    if (Object.keys(errors).length === 0) {
      approvePreProposal(id);
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Erro na validação',
        text: 'Por favor, corrija os erros no formulário antes de aprovar a pré-proposta',
      });
    }
  };

  const clearFields = (fields) => {
    fields.forEach((field) => {
      validation.setFieldValue(field, '');
    });
  };

  const handleChangeCategoryPlanInput = (e) => {
    if (e) {
      verifyIfShowInputs(e.value, 'show_organization', setShowOrganization, [
        'organization',
        'department',
        'secretary',
      ]);
      verifyIfShowInputs(
        e.value,
        'show_employment_details',
        setShowEmployment,
        ['situation', 'validContractWork', 'registration']
      );
    }

    validation.handleChange(convertEventSelectToTarget('categoryPlan', e));
  };

  const verifyIfShowInputs = useCallback(
    (idPlanCategory, property, setState, fieldsToClear) => {
      let showProperty = false;
      (categoryPlans || []).forEach((item) => {
        if (item.id === idPlanCategory && item[property] === true) {
          showProperty = true;
        } else {
          clearFields(fieldsToClear);
        }
      });
      setState(showProperty);
    },
    [categoryPlans]
  );

  useEffect(() => {
    requestGetPlans();
    requestGetKinship();
    requestGetOffices();
    requestGetOrganizations();
    requestGetCategoryPlans();
    requestGetPaymentConditions();
  }, []);

  useEffect(() => {
    if (id) {
      getPreProposalByHash(id);
    }
  }, [id]);

  useEffect(() => {
    let organizationId = validation.values.organization?.value;

    if (organizationId) {
      requestGetDepartments(organizationId);
    }
  }, [requestGetDepartments, validation.values.organization?.value]);

  useEffect(() => {
    let planId = validation.values.plan?.value;

    if (planId) {
      requestGetMaxDependentInPlans(planId);
    }
  }, [requestGetMaxDependentInPlans, validation.values.plan]);

  return (
    <LoadingScreen loading={loading} titleMetaTag={'Pré-proposta'}>
      <PageWrapper title={'Pré-proposta'}>
        <Row className="justify-content-center">
          <Col xxl={12}>
            <Card>
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                <CardHeader>
                  <Nav
                    className="nav-tabs-custom rounded card-header-tabs border-bottom-0"
                    role="tablist"
                  >
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '1' })}
                        onClick={() => {
                          tabChange('1');
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <i className="fas fa-home"></i>
                        Dados do Cliente
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '2' })}
                        onClick={() => {
                          tabChange('2');
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <i className="fas fa-home"></i>
                        Dados da Proposta
                      </NavLink>
                    </NavItem>
                  </Nav>
                </CardHeader>

                <TabContent activeTab={activeTab}>
                  <TabPane tabId={'1'}>
                    <ClientDataTab validation={validation} />
                  </TabPane>

                  <TabPane tabId="2">
                    <ProposalDataTab
                      validation={validation}
                      dependents={dependents}
                      setDependents={setDependents}
                      maxDependents={maxDependents}
                      showEmployment={showEmployment}
                      showOrganization={showOrganization}
                      situationOptions={situationOptions}
                      departmentOptions={departmentOptions}
                      isValidDependentCpf={isValidDependentCpf}
                      setIsValidDependentCpf={setIsValidDependentCpf}
                      paymentFrequencyOptions={paymentFrequencyOptions}
                      paymentConditionsOptions={paymentConditionsOptions}
                      isAnyDependentFieldRequired={isAnyDependentFieldRequired}
                      setIsAnyDependentFieldRequired={
                        setIsAnyDependentFieldRequired
                      }
                      handleChangeCategoryPlanInput={
                        handleChangeCategoryPlanInput
                      }
                      plans={plans || []}
                      offices={offices || []}
                      kinships={kinships || []}
                      organizations={organizations || []}
                      categoryPlans={categoryPlans || []}
                      preProposalByHash={preProposalByHash || null}
                    />
                  </TabPane>
                </TabContent>

                <CardFooter>
                  <Row>
                    <Col md={12}>
                      <div className="text-end">
                        <button
                          type="button"
                          className="btn btn-link"
                          onClick={() => {
                            history.goBack();
                          }}
                        >
                          Cancelar
                        </button>

                        <button
                          type="button"
                          className="btn btn-soft-warning waves-effect waves-light"
                          onClick={toggleModalPendency}
                        >
                          Pendência
                        </button>

                        <button
                          type="button"
                          disabled={isSaveButtonDisabled}
                          className="btn btn-soft-success waves-effect waves-light mx-2"
                          onClick={() => {
                            Swal.fire({
                              title: 'Confirmar aprovação?',
                              showCancelButton: true,
                              confirmButtonText: 'Sim',
                              icon: 'warning',
                              cancelButtonText: 'Não',
                              customClass: {
                                actions: 'my-actions',
                                cancelButton: 'order-2',
                                confirmButton: 'order-1 right-gap',
                                denyButton: 'order-3',
                              },
                            }).then((result) => {
                              if (result.isConfirmed) {
                                handleApprovePreProposal();
                              }
                            });
                          }}
                        >
                          Aprovar Proposta
                        </button>

                        <button
                          type="submit"
                          className="btn btn-primary"
                          // prevent submit if dependent's CPF is invalid or has any required field
                          disabled={isSaveButtonDisabled}
                        >
                          Salvar
                        </button>
                      </div>
                    </Col>
                  </Row>
                </CardFooter>
              </Form>
            </Card>
          </Col>
        </Row>

        <ModalPendency
          toggle={toggleModalPendency}
          isOpen={isOpenModalPendency}
          handleSubmit={(e) => {
            pendingPreProposal(id, {
              reason: e.target[0].value,
            });
          }}
        />
      </PageWrapper>
    </LoadingScreen>
  );
};
